import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../../components/shared/Modal';
import {
  createTask,
  updateTask,
  deleteTask,
  type TaskView,
  type TaskStatus,
  type TaskPriority,
  type CreateTaskRequest,
  type UpdateTaskRequest,
} from '../../../api/backlog';

const LABELS = ['frontend', 'backend', 'bug', 'feature', 'seo', 'infra'] as const;
const PRIORITIES: TaskPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const STATUSES: TaskStatus[] = ['TODO', 'IN_PROGRESS', 'DONE', 'CANCELLED'];

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskView | null; // null = create mode
}

const TaskModal: React.FC<TaskModalProps> = ({ isOpen, onClose, task }) => {
  const queryClient = useQueryClient();
  const isEdit = task !== null;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('MEDIUM');
  const [status, setStatus] = useState<TaskStatus>('TODO');
  const [labels, setLabels] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description ?? '');
      setPriority(task.priority);
      setStatus(task.status);
      setLabels(task.labels);
      setDueDate(task.dueDate ?? '');
    } else {
      setTitle('');
      setDescription('');
      setPriority('MEDIUM');
      setStatus('TODO');
      setLabels([]);
      setDueDate('');
    }
  }, [task, isOpen]);

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['backlog-board'] });
    onClose();
  };

  const createMutation = useMutation({ mutationFn: (req: CreateTaskRequest) => createTask(req), onSuccess });
  const updateMutation = useMutation({ mutationFn: (req: UpdateTaskRequest) => updateTask(task!.id, req), onSuccess });
  const deleteMutation = useMutation({ mutationFn: () => deleteTask(task!.id), onSuccess });

  const toggleLabel = (label: string) => {
    setLabels((prev) => prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    if (isEdit) {
      updateMutation.mutate({
        title,
        description: description || undefined,
        priority,
        status,
        labels,
        dueDate: dueDate || undefined,
      });
    } else {
      createMutation.mutate({
        title,
        description: description || undefined,
        priority,
        labels,
        dueDate: dueDate || undefined,
      });
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Task' : 'New Task'} size="md">
      <form className="task-form" onSubmit={handleSubmit}>
        <label>
          Title
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </label>

        <label>
          Description
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description..." />
        </label>

        <label>
          Priority
          <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>

        {isEdit && (
          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </label>
        )}

        <label>
          Labels
          <div className="task-form-labels">
            {LABELS.map((l) => (
              <button
                key={l}
                type="button"
                className={`task-form-label-chip ${labels.includes(l) ? 'selected' : ''}`}
                onClick={() => toggleLabel(l)}
              >
                {l}
              </button>
            ))}
          </div>
        </label>

        <label>
          Due Date
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>

        <div className="task-form-actions">
          {isEdit && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => { if (confirm('Delete this task?')) deleteMutation.mutate(); }}
              disabled={deleteMutation.isPending}
            >
              Delete
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isSaving || !title.trim()}>
            {isSaving ? 'Saving...' : isEdit ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TaskModal;
